import {
  challengeShareText,
  emojiRow,
  formatDistance,
  formatTime,
  formatYearsOff,
  MAX_BASE,
  MAX_TOTAL,
  TIER_COLOR,
  TIER_EMOJI,
  TIER_LABEL,
  tierFor,
  tierForTotal,
  type MakerScore,
  type SpecialInfo,
  specialPlayPath,
  type ReviewEntry,
  type RoundSummary,
} from '@geosport/shared';
import { useCallback, useState } from 'react';
import { Button } from '../components/Button';
import { Shell } from '../components/Shell';
import { SportIcon, sportLabel } from '../components/SportIcon';
import { Toast } from '../components/Toast';
import { track } from '../lib/analytics';
import { readChallenge } from '../lib/challenge';
import { Link, navigate } from '../lib/router';
import { useSettings } from '../lib/settings';
import { shareScoreCard } from '../lib/scoreCard';
import { shareText } from '../lib/share';
import { ReminderCard } from '../components/Reminder';
import { useCountdown } from '../lib/useCountdown';
import { useRound } from '../store/round';

function prettyDate(date: string): string {
  return new Date(`${date}T12:00:00Z`)
    .toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    .toUpperCase();
}

/** One bar per question; tap to expand the clue, answer and story. */
export function ScoreBars({
  summary,
  review,
}: {
  summary: RoundSummary;
  review: ReviewEntry[] | null;
}) {
  const units = useSettings((s) => s.units);
  const [open, setOpen] = useState<number | null>(null);
  return (
    <ol className="flex flex-col gap-2" aria-label="Points per question">
      {summary.bases.map((base, i) => {
        const tier = tierFor(base);
        const entry = review?.[i];
        const pts = summary.questionPts[i] ?? 0;
        const max = MAX_BASE * (pts && base ? pts / base : ([1, 1, 2, 3, 3][i] ?? 1));
        const expanded = open === i;
        return (
          <li key={i} className="rounded-xl bg-white/5">
            <button
              type="button"
              onClick={() => setOpen(expanded ? null : i)}
              aria-expanded={expanded}
              disabled={!entry}
              className="flex w-full items-center gap-2 p-2 text-left text-sm disabled:cursor-default"
            >
              <span className="w-7 font-semibold text-white/60">Q{i + 1}</span>
              <span role="img" aria-label={TIER_LABEL[tier]}>
                {TIER_EMOJI[tier]}
              </span>
              <div className="relative h-5 flex-1 overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full transition-[width] duration-700"
                  style={{ width: `${(pts / max) * 100}%`, backgroundColor: TIER_COLOR[tier] }}
                />
                {entry && (
                  <span className="absolute inset-y-0 left-2 flex items-center text-[11px] font-semibold text-ink mix-blend-plus-lighter">
                    <SportIcon sport={entry.sport} />
                  </span>
                )}
              </div>
              <span className="w-12 text-right font-bold tabular-nums">{pts}</span>
              {entry && (
                <svg
                  aria-hidden="true"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  className={`text-white/40 transition-transform ${expanded ? 'rotate-180' : ''}`}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              )}
            </button>
            {expanded && entry && (
              <div className="animate-fade-up px-3 pb-3 text-sm">
                <p className="text-white/60">
                  {sportLabel(entry.sport)} · {entry.clue}
                </p>
                <p className="mt-2 font-semibold">
                  {entry.answer.place} · {entry.answer.year}
                </p>
                <p className="text-xs text-white/60 tabular-nums">
                  {formatDistance(entry.distanceKm, units)} off ({entry.wherePts} where) ·{' '}
                  {formatYearsOff(entry.yearDiff)} ({entry.whenPts} when) · ×{entry.multiplier}
                </p>
                <p className="mt-2 leading-relaxed text-white/80">{entry.story}</p>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** Today's themed special, offered after the daily round. */
export function SpecialCard({ special }: { special: SpecialInfo }) {
  return (
    <Link
      to={specialPlayPath(special.slug)}
      className="mt-4 block rounded-2xl bg-accent/10 p-4 text-left ring-1 ring-accent/40 hover:ring-accent"
      data-testid="special-card"
    >
      <div className="text-xs font-bold tracking-[0.14em] text-accent">🆕 SPECIAL</div>
      <div className="mt-1 font-bold">{special.title}</div>
      <p className="mt-1 text-sm text-white/70">{special.blurb}</p>
      <span className="mt-2 inline-block text-sm font-semibold text-accent">Play it →</span>
    </Link>
  );
}

/** "Beat the maker": the maker's score on the same puzzle, once they've played it. */
export function MakerLine({ total, maker }: { total: number; maker: MakerScore }) {
  const diff = total - maker.total;
  const who = maker.name === 'The maker' ? 'The maker' : `${maker.name} (the maker)`;
  return (
    <p
      className="mt-3 rounded-xl bg-white/5 px-3 py-2 text-sm ring-1 ring-white/10"
      data-testid="maker-line"
    >
      {diff > 0
        ? `🧑‍💻 You beat the maker! ${who} scored ${maker.total}; you won by ${diff}.`
        : diff === 0
          ? `🧑‍💻 Dead level: ${who} scored ${maker.total} too.`
          : `🧑‍💻 ${who} scored ${maker.total}, ${-diff} more than you. Beat them tomorrow?`}
    </p>
  );
}

export function Results() {
  const summary = useRound((s) => s.summary);
  const review = useRound((s) => s.review);
  const phase = useRound((s) => s.phase);
  const sport = useRound((s) => s.sport);
  const activeSpecials = useRound((s) => s.activeSpecials);
  const countdown = useCountdown();
  const [toast, setToast] = useState<string | null>(null);
  const clearToast = useCallback(() => setToast(null), []);

  if (!summary) {
    return (
      <Shell title="No result yet">
        <p className="text-white/70">
          {phase === 'loading' || phase === 'boot' ? 'Loading…' : 'Play today’s round first.'}
        </p>
        <Link to="/" className="mt-4 inline-block text-accent">
          Back to home
        </Link>
      </Shell>
    );
  }

  const practice = summary.mode !== 'daily';
  const perfect = summary.total >= MAX_TOTAL;
  const totalTier = tierForTotal(summary.total);

  const onShare = async (): Promise<void> => {
    track('share_clicked', { mode: summary.mode, total: summary.total });
    const outcome = await shareText(summary.shareText);
    if (outcome === 'copied') setToast('Copied to clipboard ✓');
    else if (outcome === 'failed') setToast('Could not share');
  };

  const onShareImage = async (): Promise<void> => {
    track('share_image', { mode: summary.mode, total: summary.total });
    const outcome = await shareScoreCard(summary, summary.special?.title);
    if (outcome === 'downloaded') setToast('Image saved ✓');
    else if (outcome === 'failed') setToast('Could not make the image');
  };

  // Ranked and replayed rounds have the same five questions for everyone, so they can be a duel.
  const duel = summary.mode !== 'random';
  const challenge = duel ? readChallenge() : null;
  const versus = challenge && challenge.puzzleNo === summary.puzzleNo ? challenge.score : null;

  const onChallenge = async (): Promise<void> => {
    track('challenge_shared', {
      mode: summary.mode,
      puzzleNo: summary.puzzleNo,
      total: summary.total,
    });
    const outcome = await shareText(challengeShareText(summary.puzzleNo, summary.total));
    if (outcome === 'copied') setToast('Challenge link copied ✓');
    else if (outcome === 'failed') setToast('Could not share');
  };

  const heading = summary.special
    ? `${summary.special.title.toUpperCase()} · SPECIAL`
    : practice
      ? summary.mode === 'random'
        ? `RANDOM ROUND${sport ? ` · ${sport.toUpperCase()}` : ''} · PRACTICE`
        : `REPLAY OF PUZZLE #${summary.puzzleNo} · PRACTICE`
      : `${prettyDate(summary.date)} · PUZZLE #${summary.puzzleNo}`;

  return (
    <Shell>
      <section
        className="animate-fade-up flex flex-col items-center gap-4 text-center"
        data-testid="results-screen"
      >
        <div
          className={`w-full rounded-3xl bg-ink-2 p-5 ring-1 ring-white/10 ${perfect ? 'animate-glow' : ''}`}
        >
          <p className="text-[11px] font-bold tracking-[0.18em] text-white/50">
            {heading}
            {!practice && summary.currentStreak > 0 && ` · 🔥 ${summary.currentStreak}-DAY STREAK`}
          </p>
          <div className="mt-2 text-6xl font-black tabular-nums" data-testid="total-score">
            <span style={{ color: TIER_COLOR[totalTier] }}>{summary.total}</span>
            <span className="text-2xl text-white/40"> / {MAX_TOTAL.toLocaleString('en-GB')}</span>
          </div>
          <div className="mt-2 text-3xl tracking-wider" aria-label="Emoji result grid">
            {emojiRow(summary.bases)}
          </div>
          <p className="mt-2 text-sm text-white/60 tabular-nums">
            ⏱ {formatTime(summary.timeMs)}
            {!practice && summary.betterThanPct !== null && (
              <>
                {' '}
                · better than <strong className="text-white">{summary.betterThanPct}%</strong> of
                today’s players
              </>
            )}
          </p>

          {versus !== null && (
            <p className="mt-3 rounded-xl bg-accent/10 px-3 py-2 text-sm font-semibold ring-1 ring-accent/30">
              {summary.total > versus
                ? `🏆 You beat your friend’s ${versus} by ${summary.total - versus} points! Send it back to them.`
                : summary.total === versus
                  ? `🤝 Dead heat: you matched your friend’s ${versus}.`
                  : `Your friend’s ${versus} wins this time, by ${versus - summary.total}. Rematch tomorrow?`}
            </p>
          )}

          {summary.maker && <MakerLine total={summary.total} maker={summary.maker} />}

          <div className="mt-4 text-left">
            <ScoreBars summary={summary} review={review} />
            {review && (
              <p className="mt-1 text-center text-xs text-white/40">Tap a question to review it</p>
            )}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button onClick={() => void onShare()} data-testid="share-button">
              Share score
            </Button>
            <Button
              variant="secondary"
              onClick={() => void onShareImage()}
              data-testid="share-image-button"
            >
              📸 Share image
            </Button>
            {duel && (
              <Button
                variant="secondary"
                className="col-span-2"
                onClick={() => void onChallenge()}
                data-testid="challenge-button"
              >
                ⚔️ Challenge a friend
              </Button>
            )}
            {summary.special ? (
              <Button
                variant="secondary"
                className="col-span-2"
                onClick={() => window.location.assign('/specials')}
              >
                More specials
              </Button>
            ) : practice ? (
              <Button
                variant="secondary"
                className="col-span-2"
                onClick={() =>
                  summary.mode === 'random'
                    ? navigate(`/play/random${sport ? `?sport=${sport}` : ''}`)
                    : navigate('/practice')
                }
              >
                {summary.mode === 'random' ? 'Play another' : 'More practice'}
              </Button>
            ) : (
              <Button
                variant="secondary"
                className="col-span-2"
                onClick={() => navigate('/leaderboard')}
              >
                Leaderboard
              </Button>
            )}
          </div>
          {!practice && (
            <div className="mt-4 text-left">
              <ReminderCard />
            </div>
          )}
          {!practice && activeSpecials[0] && <SpecialCard special={activeSpecials[0]} />}
        </div>

        {!practice && (
          <div className="grid w-full grid-cols-3 gap-2 text-sm">
            <div className="rounded-xl bg-white/5 p-3">
              <div className="text-white/50">Streak</div>
              <div className="font-semibold">🔥 {summary.currentStreak}-day</div>
            </div>
            <div className="rounded-xl bg-white/5 p-3">
              <div className="text-white/50">Best</div>
              <div className="font-semibold">{summary.bestStreak}-day</div>
            </div>
            <div className="rounded-xl bg-white/5 p-3">
              <div className="text-white/50">Next round</div>
              <div className="font-semibold tabular-nums">{countdown}</div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-4 text-sm">
          {practice && (
            <Link to="/" className="text-accent">
              Today’s round
            </Link>
          )}
          <Link to="/stats" className="text-accent">
            Your stats
          </Link>
          <Link to="/groups" className="text-accent">
            Compare with friends
          </Link>
          <Link to="/practice" className="text-accent">
            Practice
          </Link>
        </div>

        {/* Phase 2: one ad slot, results page only. Hidden in the MVP. */}
        <div hidden aria-hidden="true" data-slot="results-ad" />
      </section>
      <Toast message={toast} onDone={clearToast} />
    </Shell>
  );
}
